import React from 'react';
import PropTypes from 'prop-types';

import { TopBar, Status, Dropdown } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Event from '../../../../models/eventModel';
import { usePermissions } from '../../../../user/permissions';
import LoadingCard from '../../../loadingCard';
import Style from '../topBarModules.module.css';
import { mappingColorStatus } from '../../../../pages/widgets/eventListModal';

function SwitchEventStatus({ event }) {
    const { t } = useTranslation();
    const { publication_status } = event;
    return <Status responsive color={mappingColorStatus(publication_status)} text={t(`eventStatus.${publication_status}`)} />;
}

SwitchEventStatus.propTypes = {
    event: PropTypes.object.isRequired
};

function SwitchEventItem({ event, organization, onChangeShow }) {
    const { i18n } = useTranslation();
    const history = useHistory();
    const subtitle = event.start_date ? new Intl.DateTimeFormat(i18n.language).format(new Date(event.start_date)) : null;
    return (
        <Dropdown.Item
            key={event.id}
            subtitle={subtitle}
            status={<SwitchEventStatus event={event} />}
            onClick={() => {
                history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                onChangeShow(false);
            }}
        >
            {event.name}
        </Dropdown.Item>
    );
}

SwitchEventItem.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};

export default function SwitchEvent({ organization = null, minimal = false }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [show, setShow] = React.useState(null);
    const [filteredEvents, setFilteredEvents] = React.useState([]);
    const [search, setSearch] = React.useState(null);
    const permissions = usePermissions();

    const hasCapacity = React.useMemo(() => {
        if (!organization) {
            return false;
        }
        return permissions.hasCapacities(['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT'], organization.id);
    }, [organization]);

    const today = new Date();
    const defaultQueryParams = {
        publication_status__in: 'draft,published',
        end_date: `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`,
        ordering: 'start_date'
    };
    const [, loading, sync] = Event.useApiModel(
        {
            organizationId: organization?.id,
            query: defaultQueryParams,
            launch: Boolean(organization),
            onSuccess: events => {
                setFilteredEvents(events);
            }
        },
        [organization]
    );

    const onFooterClick = React.useCallback(() => {
        history.push(`/ticket/O${organization?.id}/events`);
        setShow(false);
    }, [history, organization]);

    const onChange = React.useCallback(
        (synth, search) => {
            let _queryParams = search ? { search } : defaultQueryParams;
            setSearch(search || null);
            sync({ query: _queryParams });
        },
        [sync, defaultQueryParams]
    );

    const onReset = React.useCallback(() => {
        setSearch(null);
        sync({ query: defaultQueryParams });
    }, [sync, defaultQueryParams]);

    //Set show with null, for automatic control open/close by dropdownContainer
    const onClick = React.useCallback(() => {
        setShow(null);
    }, []);

    if (!organization || !hasCapacity || minimal) {
        return null;
    }

    return (
        <TopBar.Switch
            closeOnListClick={false}
            value={t('ticket.switchEvent.search_placeholder')}
            onLeaving={onReset}
            searchProps={{
                value: search,
                onChange: onChange,
                handleReset: onReset
            }}
            text={t('ticket.switchEvent.cta')}
            headerText={search ? t('ticket.switchEvent.header_with_search') : t('ticket.switchEvent.header')}
            footerText={t('ticket.switchEvent.footer_cta')}
            onFooterClick={onFooterClick}
            initial={show}
            onClick={onClick}
        >
            <LoadingCard loading={loading} height={'200px'}>
                {filteredEvents.length ? (
                    filteredEvents.map(event => <SwitchEventItem key={event.id} event={event} organization={organization} onChangeShow={setShow} />)
                ) : (
                    <div className={Style['switch-event__no-result']}>{t('ticket.switchEvent.no_results')}</div>
                )}
            </LoadingCard>
        </TopBar.Switch>
    );
}
